import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import EditIcon from "@mui/icons-material/Edit";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import TopNavBar from "../components/TopNavBar";
import DeviceManufacturerStrip from "../components/DeviceManufacturerStrip";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDevices } from "../api/devices";
import { getCustomers } from "../api/customer";
import { getRoomsWithHierarchy } from "../api/rooms";
import { getDeviceServicePlans } from "../api/devicePlans";

function normalizeDevices(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.devices)) return payload.devices;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeCustomers(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.customers)) return payload.customers;
  return [];
}

function normalizeRooms(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rooms)) return payload.rooms;
  return [];
}

function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

export default function ConfigureDevicePlansPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [devices, setDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [plansActiveToday, setPlansActiveToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [customerFilter, setCustomerFilter] = useState(
    () => searchParams.get("customer") || "all",
  );
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || "all",
  );
  const [showDevices, setShowDevices] = useState(true);
  const [showRooms, setShowRooms] = useState(true);

  // Keep the URL in sync so search/filters survive navigating away and back.
  useEffect(() => {
    const nextParams = {};
    if (search) nextParams.q = search;
    if (customerFilter !== "all") nextParams.customer = customerFilter;
    if (statusFilter !== "all") nextParams.status = statusFilter;
    setSearchParams(nextParams, { replace: true });
  }, [search, customerFilter, statusFilter, setSearchParams]);

  const clearFilters = () => {
    setSearch("");
    setCustomerFilter("all");
    setStatusFilter("all");
    setShowDevices(true);
    setShowRooms(true);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    const today = todayDateString();

    Promise.all([
      getDevices(),
      getRoomsWithHierarchy(),
      getCustomers(),
      getDeviceServicePlans(),
      getDeviceServicePlans({ activeOnDate: today, active: true }),
    ])
      .then(
        ([deviceData, roomData, customerData, plansData, plansTodayData]) => {
          setDevices(normalizeDevices(deviceData));
          setRooms(normalizeRooms(roomData));
          setCustomers(normalizeCustomers(customerData));
          setPlans(Array.isArray(plansData?.plans) ? plansData.plans : []);
          setPlansActiveToday(
            Array.isArray(plansTodayData?.plans) ? plansTodayData.plans : [],
          );
        },
      )
      .catch((err) => setError(err.message || "Failed to load plan allocation"))
      .finally(() => setLoading(false));
  }, []);

  // A target (device or room) can have one active plan PER service type
  // (e.g. on-site + remote), so each target maps to an array of plans.
  const plansByDeviceId = useMemo(() => {
    const map = new Map();
    plans.forEach((plan) => {
      const key = String(plan.device_id || "");
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(plan);
    });
    return map;
  }, [plans]);

  const plansByRoomId = useMemo(() => {
    const map = new Map();
    plans.forEach((plan) => {
      const key = String(plan.room_pk_id || "");
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(plan);
    });
    return map;
  }, [plans]);

  const customerNameById = useMemo(() => {
    const map = new Map();
    customers.forEach((customer) => {
      map.set(
        String(customer.pk_id),
        customer.uk_name || customer.customer_name || "Unknown",
      );
    });
    return map;
  }, [customers]);

  const activeTodayDeviceIds = useMemo(() => {
    const ids = new Set();
    plansActiveToday.forEach((plan) => {
      if (Number(plan.active) === 1 && plan.device_id) {
        ids.add(String(plan.device_id));
      }
    });
    return ids;
  }, [plansActiveToday]);

  const activeTodayRoomIds = useMemo(() => {
    const ids = new Set();
    plansActiveToday.forEach((plan) => {
      if (Number(plan.active) === 1 && plan.room_pk_id) {
        ids.add(String(plan.room_pk_id));
      }
    });
    return ids;
  }, [plansActiveToday]);

  // Unifies devices and rooms into one allocation-target list so both can be
  // searched, filtered, and assigned a plan from the same table.
  const targets = useMemo(() => {
    const deviceTargets = devices.map((device) => {
      const pkId = String(device.pk_id || "");
      const customerId = String(
        device.customer_id || device.customer_pk_id || "",
      );
      return {
        type: "device",
        pkId,
        name: device.name || device.device_name || `Device #${device.pk_id}`,
        subtitle: device.rc_device_id || "No rc_device_id",
        searchText: [
          device.name,
          device.device_name,
          device.rc_device_id,
          device.customer_name || customerNameById.get(customerId),
        ],
        customerId,
        customerName:
          device.customer_name ||
          customerNameById.get(customerId) ||
          "Unassigned",
        deviceManufacturer: device.manufacturer || null,
        plans: plansByDeviceId.get(pkId) || [],
        isActiveToday: activeTodayDeviceIds.has(pkId),
      };
    });

    const roomTargets = rooms.map((room) => {
      const pkId = String(room.pk_id || "");
      const customerId = String(room.customer_pk_id || "");
      return {
        type: "room",
        pkId,
        name: room.room_name || `Room #${room.pk_id}`,
        subtitle: [room.building_name, room.department_name]
          .filter(Boolean)
          .join(" · "),
        searchText: [
          room.room_name,
          room.department_name,
          room.building_name,
          room.location_name,
          room.customer_name,
        ],
        customerId,
        customerName:
          room.customer_name ||
          customerNameById.get(customerId) ||
          "Unassigned",
        deviceManufacturer:
          (plansByRoomId.get(pkId) || []).find(
            (plan) => plan.device_manufacturer,
          )?.device_manufacturer || null,
        plans: plansByRoomId.get(pkId) || [],
        isActiveToday: activeTodayRoomIds.has(pkId),
      };
    });

    return [...deviceTargets, ...roomTargets];
  }, [
    devices,
    rooms,
    customerNameById,
    plansByDeviceId,
    plansByRoomId,
    activeTodayDeviceIds,
    activeTodayRoomIds,
  ]);

  const filteredTargets = useMemo(() => {
    const query = String(search || "")
      .trim()
      .toLowerCase();

    return targets.filter((target) => {
      if (target.type === "device" && !showDevices) return false;
      if (target.type === "room" && !showRooms) return false;

      if (customerFilter !== "all") {
        if (target.customerId !== String(customerFilter)) return false;
      }

      if (statusFilter === "assigned" && target.plans.length === 0)
        return false;
      if (statusFilter === "unassigned" && target.plans.length > 0)
        return false;
      if (
        statusFilter === "enabled" &&
        !target.plans.some((plan) => Number(plan.active) === 1)
      )
        return false;
      if (
        statusFilter === "disabled" &&
        !target.plans.some((plan) => Number(plan.active) === 0)
      )
        return false;
      if (statusFilter === "active_today" && !target.isActiveToday)
        return false;
      if (statusFilter === "not_active_today" && target.isActiveToday)
        return false;

      if (!query) return true;

      return target.searchText
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query));
    });
  }, [targets, search, customerFilter, statusFilter, showDevices, showRooms]);

  return (
    <>
      <TopNavBar heading="Configure Plan Allocation" />
      <Container maxWidth="lg" sx={{ mt: 3, pb: 4 }}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
          <SettingsSuggestIcon sx={{ color: "text.secondary" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Configure Plan Allocation
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              label="Search robot or room"
              placeholder="Search by robot name, room, building, or customer"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        onClick={() => setSearch("")}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />

            <TextField
              select
              label="Customer"
              sx={{ minWidth: { xs: "100%", md: 260 } }}
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
            >
              <MenuItem value="all">All customers</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.pk_id} value={String(customer.pk_id)}>
                  {customer.uk_name ||
                    customer.customer_name ||
                    `Customer #${customer.pk_id}`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Assignment"
              sx={{ minWidth: { xs: "100%", md: 190 } }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
              <MenuItem value="active_today">Active today</MenuItem>
              <MenuItem value="not_active_today">Not active today</MenuItem>
            </TextField>

            <Tooltip title="Clear search and filters">
              <IconButton
                onClick={clearFilters}
                sx={{ alignSelf: { md: "center" } }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {!loading && !error && (
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                size="small"
                icon={<SmartToyOutlinedIcon fontSize="small" />}
                label="Devices"
                clickable
                color={showDevices ? "primary" : "default"}
                variant={showDevices ? "filled" : "outlined"}
                onClick={() => setShowDevices((prev) => !prev)}
              />
              <Chip
                size="small"
                icon={<MeetingRoomOutlinedIcon fontSize="small" />}
                label="Rooms"
                clickable
                color={showRooms ? "primary" : "default"}
                variant={showRooms ? "filled" : "outlined"}
                onClick={() => setShowRooms((prev) => !prev)}
              />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, textAlign: "right" }}
            >
              {filteredTargets.length} of {targets.length} target
              {targets.length === 1 ? "" : "s"} shown
            </Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && filteredTargets.length === 0 && (
          <Typography color="text.secondary">
            No matching robots or rooms found.
          </Typography>
        )}

        {!loading && !error && filteredTargets.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Robot / Room</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Service Plan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Route Plan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Active Today
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Overrides
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTargets.map((target) => (
                  <TableRow
                    key={`${target.type}-${target.pkId}`}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(
                        `/configure-plan-allocation/${target.type}/${target.pkId}`,
                      )
                    }
                  >
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {target.type === "device" ? (
                          <SmartToyOutlinedIcon
                            fontSize="small"
                            color="action"
                          />
                        ) : (
                          <MeetingRoomOutlinedIcon
                            fontSize="small"
                            color="action"
                          />
                        )}
                        <DeviceManufacturerStrip
                          manufacturers={
                            target.deviceManufacturer
                              ? [target.deviceManufacturer]
                              : []
                          }
                          size={18}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {target.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {target.subtitle || "-"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{target.customerName}</TableCell>
                    <TableCell>
                      {target.plans.length === 0 ? (
                        "Not assigned"
                      ) : (
                        <Stack spacing={0.5}>
                          {target.plans.map((plan) => (
                            <Box
                              key={plan.pk_id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {plan.service_type_name || "Service plan"}:
                              </Typography>
                              <Typography variant="body2">
                                {plan.service_plan_template_name ||
                                  "Not assigned"}
                              </Typography>
                              <Chip
                                label={
                                  Number(plan.active) === 1
                                    ? "Enabled"
                                    : "Disabled"
                                }
                                size="small"
                                color={
                                  Number(plan.active) === 1
                                    ? "success"
                                    : "warning"
                                }
                                variant="outlined"
                              />
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      {target.plans.length === 0 ? (
                        "Not assigned"
                      ) : (
                        <Stack spacing={0.5}>
                          {target.plans.map((plan) => (
                            <Typography key={plan.pk_id} variant="body2">
                              {plan.route_template_name || "Not assigned"}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={target.isActiveToday ? "Yes" : "No"}
                        size="small"
                        color={target.isActiveToday ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {target.plans.reduce(
                        (sum, plan) => sum + (plan.overrides_count || 0),
                        0,
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={
                          target.plans.length > 0
                            ? "Edit assignment"
                            : "Create assignment"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(
                              `/configure-plan-allocation/${target.type}/${target.pkId}`,
                            );
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>
    </>
  );
}
